import { RefreshCw, Loader2, Newspaper, LogOut, Settings, Shield, Plus } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { NewsCard, SkeletonCard } from "@/components/news/NewsCard";
import { SourcesPanel } from "@/components/news/SourcesPanel";
import { FilterBar } from "@/components/news/FilterBar";
import { Footer } from "@/components/Footer";
import { FaqAccordion } from "@/components/FaqAccordion";
import { HighlightsCarousel } from "@/components/news/HighlightsCarousel";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useArticleFeed } from "@/hooks/useArticleFeed";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/newsUtils";
import { useSeo } from "@/lib/seo";

export default function Home() {
  const { user, logout } = useAuth();
  const [homeCfg, setHomeCfg] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pages/home`);
        setHomeCfg(data);
      } catch { /* silencioso */ }
    })();
  }, []);
  useSeo({
    title: homeCfg?.meta_title || "News Digest — Tus medios resumidos por IA",
    description: homeCfg?.meta_description || "Reúne y resume con IA las noticias de los medios que eliges. Todo tu contenido en un solo feed.",
  });
  const {
    articles,
    sources,
    loading,
    loadingMore,
    hasMore,
    refreshing,
    loadMore,
    refresh,
    fetchSources,
    clearFilters,
    hasActiveFilters,
    filters,
    set,
  } = useArticleFeed();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary flex items-center justify-center shrink-0">
              <Newspaper className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight leading-none whitespace-nowrap">
                NEWS DIGEST
              </h1>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
                Tus medios · Resúmenes Gemini
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden md:inline text-sm text-muted-foreground">
              Hola, <span className="font-semibold text-foreground">{user?.username}</span>
            </span>
            <ThemeToggle />
            <SourcesPanel sources={sources} onChanged={fetchSources} />
            {user?.role === "admin" && (
              <Link to="/admin">
                <Button
                  data-testid="admin-link"
                  variant="outline"
                  className="rounded-none border-primary text-primary hover:bg-primary hover:text-white h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              </Link>
            )}
            <Link to="/ajustes">
              <Button
                data-testid="settings-link"
                variant="outline"
                className="rounded-none border-border h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Ajustes</span>
              </Button>
            </Link>
            <Button
              data-testid="refresh-button"
              onClick={refresh}
              disabled={refreshing}
              className="rounded-none bg-primary hover:bg-blue-900 h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Actualizar</span>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="logout-button"
                  variant="outline"
                  className="rounded-none border-border h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Salir</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none" data-testid="logout-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Cerrar sesión?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se cerrará tu sesión y volverás a la pantalla de inicio de sesión.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="logout-cancel" className="rounded-none">
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="logout-confirm"
                    onClick={logout}
                    className="rounded-none bg-primary hover:bg-blue-900"
                  >
                    Sí, salir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <div className="border-b border-border bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <h2 className="font-heading text-3xl sm:text-5xl font-black tracking-tight max-w-3xl leading-[1.05]">
            Tus medios, resumidos por IA. En un solo feed.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl text-base">
            Reunimos las últimas noticias de los medios que sigues y las condensamos en
            resúmenes claros con Gemini. Tú decides qué leer entero.
          </p>
        </div>
      </div>

      <FilterBar
        sources={sources}
        filters={filters}
        set={set}
        hasActiveFilters={hasActiveFilters}
        clearFilters={clearFilters}
      />

      {/* Feed */}
      <main className="max-w-7xl mx-auto px-6 py-10" data-testid="news-feed">
        <div className="mb-8">
          <HighlightsCarousel />
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-24" data-testid="empty-state">
            <Newspaper className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="font-heading text-xl font-bold">No hay noticias que coincidan.</p>
            <p className="text-muted-foreground mt-2">
              Ajusta o limpia los filtros, o pulsa «Actualizar» para traer más noticias.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {articles.map((a, i) => (
              <NewsCard key={a.id} article={a} index={i} />
            ))}
          </div>
        )}

        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground" data-testid="loading-more">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm uppercase tracking-widest font-medium">
              Cargando más noticias...
            </span>
          </div>
        )}

        {!loading && !loadingMore && hasMore && articles.length > 0 && (
          <div className="flex justify-center py-10">
            <Button
              data-testid="load-more-button"
              onClick={loadMore}
              className="rounded-none bg-primary hover:bg-blue-900 h-12 px-8 gap-2"
            >
              <Plus className="w-4 h-4" />
              Leer más noticias
            </Button>
          </div>
        )}

        {!hasMore && articles.length > 0 && (
          <p className="text-center py-8 text-xs uppercase tracking-widest text-muted-foreground font-medium">
            — Has visto todas las noticias —
          </p>
        )}
      </main>

      {!loading && <FaqAccordion sections={homeCfg?.sections} />}
      {!loading && <Footer />}
    </div>
  );
}
